import { z } from 'zod';
import { apiKeyId, auditEventId, membershipId, orgId, roleId, sessionId, userId, } from '../ids.js';
import { homeRegion, orgStatus, principalType, roleName } from '../enums.js';
/** Garnet PRD §5.1, §16. */
export const Org = z.object({
    id: orgId,
    legal_name: z.string().min(1),
    home_region: homeRegion,
    residency_policy: z.object({
        /** Sensitive data may never be processed by a third-party model provider. */
        forbid_third_party_processing: z.boolean(),
        /** Storage and processing pinned to the home cell. Always true; explicit for audit. */
        region_pinned: z.literal(true),
    }),
    plan: z.enum(['starter', 'growth', 'scale', 'enterprise']),
    status: orgStatus,
    created_at: z.string().datetime({ offset: false }),
});
export const User = z.object({
    id: userId,
    email: z.string().email(),
    display_name: z.string().min(1),
    totp_enrolled: z.boolean(),
    status: z.enum(['active', 'invited', 'disabled']),
    created_at: z.string().datetime(),
});
/**
 * Capability matrix (PRD §16.3). Enforced at the gateway AND re-evaluated in the owning
 * module. The advisor service principal appears in NO entry of any decision capability —
 * that is Invariant I3, structural.
 */
export const capability = z.enum([
    'counterparty.create',
    'claim.create',
    'record.read',
    'record.share_restricted',
    'record.share_authority',
    'case.adjudicate',
    'case.request_counterparty_input',
    'case.signoff_blocking',
    'decision.make',
    'decision.override_blocking',
    'tier.override',
    'org.manage_users',
    'org.manage_api_keys',
    'org.manage_billing',
    'org.manage_residency',
    'inspector.app',
    'admin.platform',
]);
export const ROLE_CAPABILITIES = {
    operator: ['counterparty.create', 'claim.create', 'record.read', 'record.share_restricted', 'decision.make'],
    reviewer: [
        'counterparty.create',
        'claim.create',
        'record.read',
        'record.share_restricted',
        'decision.make',
        'case.adjudicate',
        'case.request_counterparty_input',
    ],
    compliance_approver: [
        'counterparty.create',
        'claim.create',
        'record.read',
        'record.share_restricted',
        'record.share_authority',
        'decision.make',
        'decision.override_blocking',
        'case.adjudicate',
        'case.request_counterparty_input',
        'case.signoff_blocking',
        'tier.override',
    ],
    org_admin: [
        'record.read',
        'org.manage_users',
        'org.manage_api_keys',
        'org.manage_billing',
        'org.manage_residency',
    ],
    read_only: ['record.read'],
    inspector: ['inspector.app'],
    platform_admin: ['admin.platform'],
};
/** Capabilities that mutate decision state. No service principal may ever hold one. */
export const DECISION_CAPABILITIES = [
    'case.adjudicate',
    'case.signoff_blocking',
    'decision.make',
    'decision.override_blocking',
    'tier.override',
];
export const Role = z.object({
    id: roleId,
    org_id: orgId,
    name: roleName,
});
export const Membership = z.object({
    id: membershipId,
    org_id: orgId,
    user_id: userId,
    role: roleName,
    created_at: z.string().datetime(),
});
export const ApiKey = z.object({
    id: apiKeyId,
    org_id: orgId,
    /** Hash only; the secret is shown once at mint (CLAUDE.md §2.6). */
    key_hash: z.string(),
    name: z.string(),
    /** Subset of the granting user's capabilities (PRD §16.3). */
    capabilities: z.array(capability),
    granted_by: userId,
    last_used_at: z.string().datetime().nullable(),
    expires_at: z.string().datetime().nullable(),
    revoked_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
});
export const Session = z.object({
    id: sessionId,
    user_id: userId,
    org_id: orgId,
    /** Step-up timestamp: sensitive actions require recent re-auth (CLAUDE.md §2.7). */
    last_strong_auth_at: z.string().datetime(),
    device: z.string().nullable(),
    ip: z.string().nullable(),
    expires_at: z.string().datetime(),
    created_at: z.string().datetime(),
});
/** The authenticated caller, resolved by the gateway, threaded through every module. */
export const Principal = z.object({
    type: principalType,
    /** user id, api key id, or service name (e.g. "advisor", "pulse"). */
    subject: z.string(),
    org_id: orgId.nullable(),
    capabilities: z.array(capability),
});
/** Append-only (PRD §16.4). Application DB roles hold INSERT and SELECT only. */
export const AuditEvent = z.object({
    id: auditEventId,
    org_id: orgId.nullable(),
    actor_type: principalType,
    actor: z.string(),
    action: z.string().min(1),
    target_kind: z.string(),
    target_id: z.string(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    ip: z.string().nullable(),
    at: z.string().datetime(),
});
//# sourceMappingURL=iam.js.map